import axios from "axios";
import { getSettings } from "./settings";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL !== undefined 
  ? process.env.REACT_APP_BACKEND_URL 
  : (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" ? "http://localhost:8000" : "");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({ baseURL: API });

// Dynamic Request Interceptor to inject Supabase Auth JWT and settings headers
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const s = getSettings();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  config.headers["X-Provider"] = s.provider || "anthropic";
  config.headers["X-Model"] = s.model || "claude-sonnet-4-5-20250929";
  config.headers["X-Api-Key"] = (s.apiKey || "").trim();

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response Interceptor to handle expired JWT auth tokens (401 Unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const detail = error.response.data?.detail || "";
      if (
        detail.includes("Invalid authorization token") ||
        detail.includes("Signature has expired") ||
        detail.includes("Missing or invalid authorization")
      ) {
        supabase.auth.signOut();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login?expired=1";
        }
      }
    }
    return Promise.reject(error);
  }
);

export const Recruit = {
  providers: () => api.get("/providers").then(r => r.data),
  listModels: (provider, apiKey) => api.get(`/llm/models?provider=${provider}`, { headers: { "X-Api-Key": apiKey || "" } }).then(r => r.data),
  trialStatus: () => api.get("/trial/status").then(r => r.data),
  jdExtract: (jd_text) => api.post("/jd/extract", { jd_text }).then(r => r.data),
  parseJDFile: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/jd/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  listProjects: () => api.get("/projects").then(r => r.data),
  createProject: (payload) => api.post("/projects", payload).then(r => r.data),
  getProject: (id) => api.get(`/projects/${id}`).then(r => r.data),
  deleteProject: (id) => api.delete(`/projects/${id}`).then(r => r.data),
  uploadJD: (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post(`/projects/${id}/upload-jd`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  uploadResumes: async (id, files, onProgress = null) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return { uploaded: [] };

    // Batch in chunks of 4 to stay strictly within AWS API Gateway's 10 MB payload ceiling
    const CHUNK_SIZE = 4;
    let allUploaded = [];
    const totalChunks = Math.ceil(fileArray.length / CHUNK_SIZE);

    for (let i = 0; i < fileArray.length; i += CHUNK_SIZE) {
      const chunk = fileArray.slice(i, i + CHUNK_SIZE);
      const chunkIndex = Math.floor(i / CHUNK_SIZE) + 1;

      if (onProgress) {
        onProgress({
          currentChunk: chunkIndex,
          totalChunks,
          uploadedCount: allUploaded.length,
          totalFiles: fileArray.length,
        });
      }

      const fd = new FormData();
      chunk.forEach(f => fd.append("files", f));

      const res = await api.post(`/projects/${id}/upload-resumes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.uploaded) {
        allUploaded = [...allUploaded, ...res.data.uploaded];
      }
    }

    return { uploaded: allUploaded };
  },
  deleteCandidate: (pid, cid) => api.delete(`/projects/${pid}/candidates/${cid}`).then(r => r.data),
  setLinks: (pid, cid, links) => api.patch(`/projects/${pid}/candidates/${cid}/links`, links).then(r => r.data),
  analyze: (id, opts = null) => {
    const params = new URLSearchParams();
    if (typeof opts === "number") {
      params.set("batch_size", String(opts));
    } else if (opts && typeof opts === "object") {
      if (opts.batchSize) params.set("batch_size", String(opts.batchSize));
      if (opts.candidateIds?.length) params.set("candidate_ids", opts.candidateIds.join(","));
      if (opts.skipIds?.length) params.set("skip_ids", opts.skipIds.join(","));
    }
    const qs = params.toString();
    const url = qs ? `/projects/${id}/analyze?${qs}` : `/projects/${id}/analyze`;
    return api.post(url, null).then(r => r.data);
  },
  analyzeCandidate: (pid, cid) => api.post(`/projects/${pid}/candidates/${cid}/analyze`, null).then(r => r.data),
  interviewKit: (pid, cid) => api.post(`/projects/${pid}/candidates/${cid}/interview-kit`, null).then(r => r.data),
  generateQuestionAnswers: (pid, cid, qidx) => api.post(`/projects/${pid}/candidates/${cid}/interview-kit/questions/${qidx}/answers`, null).then(r => r.data),
  
  // Privacy Setting Operations
  getPrivacy: () => api.get("/settings/privacy").then(r => r.data),
  updatePrivacy: (delete_resume_after_parse) => api.patch("/settings/privacy", { delete_resume_after_parse }).then(r => r.data),
  
  // Stripe Checkout Integration & Beta Evaluation Grants
  claimBetaCredits: () => api.post("/checkout/claim-beta-credits").then(r => r.data),
  createCheckoutSession: () => api.post("/checkout/create-session").then(r => r.data),
  
  // Asset Downloader URLs (token passed as query parameter)
  exportCsvUrl: (id, token, candidateIds = null) => {
    const cids = candidateIds?.length ? `&candidate_ids=${encodeURIComponent(candidateIds.join(","))}` : "";
    return `${API}/projects/${id}/export.csv?token=${token}${cids}`;
  },
  exportPdfUrl: (id, token, candidateIds = null) => {
    const cids = candidateIds?.length ? `&candidate_ids=${encodeURIComponent(candidateIds.join(","))}` : "";
    return `${API}/projects/${id}/export.pdf?token=${token}${cids}`;
  },
  exportPackZipUrl: (id, token, candidateIds = null) => {
    const cids = candidateIds?.length ? `&candidate_ids=${encodeURIComponent(candidateIds.join(","))}` : "";
    return `${API}/projects/${id}/export-pack.zip?token=${token}${cids}`;
  },
  exportHtmlUrl: (id, token, candidateIds = null) => {
    const cids = candidateIds?.length ? `&candidate_ids=${encodeURIComponent(candidateIds.join(","))}` : "";
    return `${API}/projects/${id}/export-report.html?token=${token}${cids}`;
  },
  candidateBriefUrl: (pid, cid, token) => `${API}/projects/${pid}/candidates/${cid}/export.pdf?token=${token}`,
  candidateResumeUrl: (pid, cid, token, inline = false) => `${API}/projects/${pid}/candidates/${cid}/resume?token=${token}${inline ? "&inline=1" : ""}`,
};
